import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { AuthProvider, usePermissionValidation } from "./context/ApiKeyContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeModeProvider } from "./context/ThemeContext";
import LoginPage from "./components/LoginPage";
import MainLayout from "./layouts/MainLayout";
import Dashboard from "./pages/Dashboard";
import EditorTemplates from "./pages/EditorTemplates";
import Contestacao from "./pages/Contestacao";
import ContestacaoDossieCreate from "./pages/ContestacaoDossieCreate";
import ProcessamentosPage from "./pages/ProcessamentosPage";
import ContestacaoPage from "./pages/ContestacaoPage";
import ContestacaoDossiePage from "./pages/ContestacaoDossiePage";
import AccountPage from "./pages/AccountPage";
import AccountPageSimple from "./pages/AccountPageSimple";
import UserManagement from "./pages/UserManagement";
import DocumentReviewPage from "./pages/DocumentReviewPage";
import EmbedBootstrapPage from "./pages/EmbedBootstrapPage";
import TranscriptionReviewPage from "./pages/TranscriptionReviewPage";
import JobStatusViewerPage from "./pages/JobStatusViewerPage";
import EnvironmentManagementPage from "./pages/EnvironmentManagementPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import { WizardProvider } from "./wizard/WizardContext";
import WizardOverlay from "./wizard/WizardOverlay";
import ConsumptionReportPage from "./pages/ConsumptionReportPage";
import "./App.css";

const theme = createTheme({
  palette: {
    primary: {
      main: "rgb(216, 78, 43)", // Laranja principal da marca
    },
    secondary: {
      main: "#643184", // Roxo para contraste
    },
    background: {
      default: "#f5f5f5", // Cinza claro para fundos
    },
    success: { main: "#4caf50" }, // Verde para sucesso
    error: { main: "#f44336" }, // Vermelho para erros
    warning: { main: "#ff9800" }, // Laranja para avisos
    info: { main: "#2196f3" }, // Azul para informações
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar se existe um token de autenticação salvo no localStorage
  useEffect(() => {
    const token = localStorage.getItem("ikaro_auth_token");
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // Componente para validar permissões
  const PermissionValidator = ({ children }) => {
    usePermissionValidation();
    return children;
  };

  // Componente para rotas protegidas que requerem autenticação
  const PrivateRoute = ({ children, ...rest }) => {
    return (
      <Route
        {...rest}
        render={({ location }) =>
          isAuthenticated ? (
            <PermissionValidator>{children}</PermissionValidator>
          ) : (
            <Redirect
              to={{
                pathname: "/login",
                state: { from: location },
              }}
            />
          )
        }
      />
    );
  };

  return (
    <ThemeModeProvider>
      <LanguageProvider>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <WizardProvider>
            <WizardOverlay />
            <Router>
              <div className="App">
                <Switch>
                  <Route path="/embed">
                    <EmbedBootstrapPage
                      onAuthenticated={() => setIsAuthenticated(true)}
                    />
                  </Route>

                  <Route path="/login">
                    {isAuthenticated ? (
                      <Redirect to="/dashboard" />
                    ) : (
                      <LoginPage
                        onLoginSuccess={() => setIsAuthenticated(true)}
                      />
                    )}
                  </Route>

                  <Route path="/account">
                    <AccountPage />
                  </Route>

                  <Route path="/account-test">
                    <AccountPageSimple />
                  </Route>

                  <PrivateRoute path="/dashboard">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                    >
                      <Dashboard />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/base-conhecimento">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <KnowledgeBasePage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/editor-templates">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                    >
                      <EditorTemplates />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/processamentos">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <ProcessamentosPage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/contestacoes">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <ContestacaoPage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/contestacao-dossie/nova">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                    >
                      <ContestacaoDossieCreate />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute exact path="/contestacao-dossie">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <ContestacaoDossiePage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/contestacao/nova">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                    >
                      <Contestacao />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/processamento/novo">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                    >
                      <EditorTemplates />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/documento/revisar/:documentType/:documentId">
                    <DocumentReviewPage />
                  </PrivateRoute>

                  <PrivateRoute path="/transcricao/revisar/:jobId">
                    <TranscriptionReviewPage />
                  </PrivateRoute>

                  <PrivateRoute path="/status-tarefas">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <JobStatusViewerPage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/relatorios-consumo">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                    >
                      <ConsumptionReportPage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/gestao-ambientes">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <EnvironmentManagementPage />
                    </MainLayout>
                  </PrivateRoute>

                  <PrivateRoute path="/usuarios">
                    <MainLayout
                      isAuthenticated={isAuthenticated}
                      setIsAuthenticated={setIsAuthenticated}
                      compact={true}
                    >
                      <UserManagement />
                    </MainLayout>
                  </PrivateRoute>

                  <Route exact path="/">
                    <Redirect to={isAuthenticated ? "/dashboard" : "/login"} />
                  </Route>
                </Switch>
              </div>
            </Router>
            </WizardProvider>
          </ThemeProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeModeProvider>
  );
}

export default App;
